import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { getTransactionHistory, changeTransactionStatusApi } from "../api/api";
import "./history.css";

interface Transaction {
  transactions_id: string | number;
  beneficiary?: {
    account_holder_name?: string;
    account_number?: string;
    bank_name?: string;
    customer?: {
      mobile_number?: string;
      customer_name?: string;
    };
  };
  user_amount: string | number;
  user_amount_with_charge: string | number;
  commission: string | number; 
  transaction_id: string | number;
  transaction_status: "pending" | "success" | "failed" | string;
  transaction_date: string;
}

type SortCol =
  | "id"
  | "beneficiary"
  | "amount"
  | "charge"
  | "transaction_id"
  | "status"
  | "date";

interface ColFilters {
  id: string;
  beneficiary: string;
  amount: string;
  charge: string;
  transaction_id: string;
  status: string;
  date: string;
}

const INITIAL_COL_FILTERS: ColFilters = {
  id: "",
  beneficiary: "",
  amount: "",
  charge: "",
  transaction_id: "",
  status: "",
  date: "",
};

export default function HistoryPage() {
  const { user } = useAuth() as any;

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);

  // Global bar filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState("");

  // Per-column filters
  const [colFilters, setColFilters] = useState<ColFilters>(INITIAL_COL_FILTERS);

  // Sorting
  const [sortCol, setSortCol] = useState<SortCol | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);

  // Reject state
  const [rejectingId, setRejectingId] = useState<string | number | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [rejectSuccess, setRejectSuccess] = useState<string | null>(null);

  // Confirm popup
  const [confirmTxn, setConfirmTxn] = useState<Transaction | null>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const doFetch = useCallback(async () => {
    if (!user?.token) {
      setError("Not authenticated");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        login_id: user.id,
        page,
        per_page: perPage,
        search,
        status: statusFilter,
      };
      const body = await getTransactionHistory(payload, user.token);
      if (!body?.success) {
        setTransactions([]);
        setError(body?.message || "Failed to fetch history");
        return;
      }
      const responseData = body.data ?? body;
      const fetchedTransactions: Transaction[] = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData?.transactions)
        ? responseData.transactions
        : [];
      setTransactions(fetchedTransactions);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Request failed");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, perPage, user?.token, user?.id]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  // ── Reject ─────────────────────────────────────────────────────────────────
  const handleRejectConfirm = async () => {
    if (!confirmTxn) return;
    setRejectingId(confirmTxn.transactions_id);
    setRejectError(null);
    setRejectSuccess(null);
    setConfirmTxn(null);
    try {
      const payload = {
        transactions_id: confirmTxn.transactions_id,   // ← was "transaction_id", API needs "transactions_id"
        transaction_status: "partially-failed",                  // ← was "status", match ApprovePaymentPage payload
        transaction_no: `TXN${confirmTxn.transactions_id}`, // ← required by API
        login_id: user?.id ?? 0,
      };
      const body = await changeTransactionStatusApi(payload, user?.token);
      if (!body?.success) {
        throw new Error(body?.message || "Failed to update transaction status");
      }
      setRejectSuccess(`Transaction #${confirmTxn.transactions_id} rejected.`);
      // Update row locally so UI reflects change instantly
      setTransactions((prev) =>
        prev.map((t) =>
          t.transactions_id === confirmTxn.transactions_id
            ? { ...t, transaction_status: "failed" }
            : t
        )
      );
    } catch (e: unknown) {
      setRejectError(e instanceof Error ? e.message : "Failed to reject");
    } finally {
      setRejectingId(null);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const handleSort = useCallback((col: SortCol) => {
    setSortCol((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === 1 ? -1 : 1));
        return col;
      }
      setSortDir(1);
      return col;
    });
  }, []);

  const sortIcon = (col: SortCol) => {
    if (sortCol !== col) return "↕";
    return sortDir === 1 ? "↑" : "↓";
  };

  const setColFilter = (key: keyof ColFilters, value: string) =>
    setColFilters((prev) => ({ ...prev, [key]: value }));

  const handleReset = () => {
    setSearch("");
    setStatusFilter("");
    setDateRange("");
    setColFilters(INITIAL_COL_FILTERS);
    setSortCol(null);
    setSortDir(1);
    setPage(1);
    setRejectError(null);
    setRejectSuccess(null);
  };

  // ── Client-side filter + sort ──────────────────────────────────────────────
  const displayRows = (() => {
    let rows = [...transactions];

    if (colFilters.id)
      rows = rows.filter((t) =>
        String(t.transactions_id)
          .toLowerCase()
          .includes(colFilters.id.toLowerCase())
      );
    if (colFilters.beneficiary)
      rows = rows.filter((t) =>
        (t.beneficiary?.account_holder_name ?? "")
          .toLowerCase()
          .includes(colFilters.beneficiary.toLowerCase())
      );
    if (colFilters.amount)
      rows = rows.filter((t) =>
        String(t.user_amount).includes(colFilters.amount)
      );
    if (colFilters.charge)
  rows = rows.filter((t) =>
    String(t.commission).includes(colFilters.charge)
  );
    if (colFilters.transaction_id)
      rows = rows.filter((t) =>
        String(t.transaction_id).includes(colFilters.transaction_id)
      );
    if (colFilters.status)
      rows = rows.filter(
        (t) =>
          t.transaction_status?.toLowerCase() ===
          colFilters.status.toLowerCase()
      );
    if (colFilters.date)
      rows = rows.filter((t) =>
        new Date(t.transaction_date)
          .toLocaleString()
          .toLowerCase()
          .includes(colFilters.date.toLowerCase())
      );

    if (sortCol) {
      rows.sort((a, b) => {
        let aVal = "";
        let bVal = "";
        if (sortCol === "id") {
          aVal = String(a.transactions_id);
          bVal = String(b.transactions_id);
        } else if (sortCol === "beneficiary") {
          aVal = a.beneficiary?.account_holder_name ?? "";
          bVal = b.beneficiary?.account_holder_name ?? "";
        } else if (sortCol === "amount") {
          aVal = String(a.user_amount);
          bVal = String(b.user_amount);
        } else if (sortCol === "charge") {
          aVal = String(a.user_amount_with_charge);
          bVal = String(b.user_amount_with_charge);
        } else if (sortCol === "transaction_id") {
          aVal = String(a.transaction_id);
          bVal = String(b.transaction_id);
        } else if (sortCol === "status") {
          aVal = a.transaction_status;
          bVal = b.transaction_status;
        } else if (sortCol === "date") {
          aVal = a.transaction_date;
          bVal = b.transaction_date;
        }
        return aVal < bVal ? -sortDir : aVal > bVal ? sortDir : 0;
      });
    }
    return rows;
  })();

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="history-wrap">
      {/* Header */}
      <div className="page-header-hero">
        <div>
          <div className="title">Transaction History</div>
          <div className="subtitle">
            View and track all your transactions in one place.
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls">
        <div className="search">
          <input
            type="text"
            placeholder="Search by beneficiary or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>
        <div className="daterange">
          <input
            type="text"
            placeholder="01 Jun 2025 - 30 Jun 2025"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          />
        </div>
        <button className="btn" onClick={() => setPage(1)}>
          Apply
        </button>
        <button className="btn secondary" onClick={handleReset}>
          Reset
        </button>
      </div>

      {/* Feedback banners */}
      {error && <div style={fb.err}>{error}</div>}
      {rejectError && <div style={fb.err}>{rejectError}</div>}
      {rejectSuccess && (
        <div style={fb.ok}>
          ✓ {rejectSuccess}
          <button style={fb.closeBtn} onClick={() => setRejectSuccess(null)}>
            ×
          </button>
        </div>
      )}

      {/* Table card */}
      <div className="card history-card">
        <div className="table-wrap">
          {loading ? (
            <div style={{ padding: 16 }}>Loading…</div>
          ) : (
            <table className="history-table">
              <thead>
                <tr>
                  {/* # */}
                  <th>
                    <div className="th-inner">
                      <div
                        className="th-label"
                        onClick={() => handleSort("id")}
                      >
                        #{" "}
                        <span
                          className={`sort-icon${
                            sortCol === "id" ? " active" : ""
                          }`}
                        >
                          {sortIcon("id")}
                        </span>
                      </div>
                      <input
                        className="col-filter"
                        placeholder="Filter…"
                        value={colFilters.id}
                        onChange={(e) => setColFilter("id", e.target.value)}
                      />
                    </div>
                  </th>

                  {/* Customer Mobile */}
                  <th>
                    <div className="th-inner">
                      <div className="th-label">Customer Mobile</div>
                      <input
                        className="col-filter"
                        placeholder="Filter…"
                        value={colFilters.beneficiary}
                        onChange={(e) =>
                          setColFilter("beneficiary", e.target.value)
                        }
                      />
                    </div>
                  </th>

                  {/* Bank Name */}
                  <th>
                    <div className="th-inner">
                      <div className="th-label">Bank Name</div>
                      <input
                        className="col-filter"
                        placeholder="Filter…"
                        value={colFilters.beneficiary}
                        onChange={(e) =>
                          setColFilter("beneficiary", e.target.value)
                        }
                      />
                    </div>
                  </th>

                  {/* Account Number */}
                  <th>
                    <div className="th-inner">
                      <div className="th-label">Account Number</div>
                      <input
                        className="col-filter"
                        placeholder="Filter…"
                        value={colFilters.beneficiary}
                        onChange={(e) =>
                          setColFilter("beneficiary", e.target.value)
                        }
                      />
                    </div>
                  </th>

                  {/* Amount */}
                  <th>
                    <div className="th-inner">
                      <div
                        className="th-label"
                        onClick={() => handleSort("amount")}
                      >
                        Amount{" "}
                        <span
                          className={`sort-icon${
                            sortCol === "amount" ? " active" : ""
                          }`}
                        >
                          {sortIcon("amount")}
                        </span>
                      </div>
                      <input
                        className="col-filter"
                        placeholder="Filter…"
                        value={colFilters.amount}
                        onChange={(e) =>
                          setColFilter("amount", e.target.value)
                        }
                      />
                    </div>
                  </th>

                  {/* Charge */}
                  <th>
                    <div className="th-inner">
                      <div
                        className="th-label"
                        onClick={() => handleSort("charge")}
                      >
                        Charge{" "}
                        <span
                          className={`sort-icon${
                            sortCol === "charge" ? " active" : ""
                          }`}
                        >
                          {sortIcon("charge")}
                        </span>
                      </div>
                      <input
                        className="col-filter"
                        placeholder="Filter…"
                        value={colFilters.charge}
                        onChange={(e) =>
                          setColFilter("charge", e.target.value)
                        }
                      />
                    </div>
                  </th>

                  {/* Status */}
                  <th>
                    <div className="th-inner">
                      <div
                        className="th-label"
                        onClick={() => handleSort("status")}
                      >
                        Status{" "}
                        <span
                          className={`sort-icon${
                            sortCol === "status" ? " active" : ""
                          }`}
                        >
                          {sortIcon("status")}
                        </span>
                      </div>
                      <select
                        className="col-filter"
                        value={colFilters.status}
                        onChange={(e) =>
                          setColFilter("status", e.target.value)
                        }
                      >
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="success">Success</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>
                  </th>

                  {/* Transaction ID */}
                  <th>
                    <div className="th-inner">
                      <div
                        className="th-label"
                        onClick={() => handleSort("transaction_id")}
                      >
                        Transaction ID{" "}
                        <span
                          className={`sort-icon${
                            sortCol === "transaction_id" ? " active" : ""
                          }`}
                        >
                          {sortIcon("transaction_id")}
                        </span>
                      </div>
                      <input
                        className="col-filter"
                        placeholder="Filter…"
                        value={colFilters.transaction_id}
                        onChange={(e) =>
                          setColFilter("transaction_id", e.target.value)
                        }
                      />
                    </div>
                  </th>

                  {/* Date */}
                  <th>
                    <div className="th-inner">
                      <div
                        className="th-label"
                        onClick={() => handleSort("date")}
                      >
                        Date &amp; Time{" "}
                        <span
                          className={`sort-icon${
                            sortCol === "date" ? " active" : ""
                          }`}
                        >
                          {sortIcon("date")}
                        </span>
                      </div>
                      <input
                        className="col-filter"
                        placeholder="Filter…"
                        value={colFilters.date}
                        onChange={(e) => setColFilter("date", e.target.value)}
                      />
                    </div>
                  </th>

                  {/* Action */}
                  <th>
                    <div className="th-inner">
                      <div className="th-label">Action</div>
                      <div style={{ height: 28 }} />
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {displayRows.map((t) => {
                  // FIX 1: case-insensitive check so "Pending", "PENDING" all work
                  const isPending =
                    t.transaction_status?.toLowerCase() === "pending";
                  const isRejecting = rejectingId === t.transactions_id;

                  return (
                    <tr key={t.transactions_id}>
                      <td>{`#${t.transactions_id}`}</td>
                      <td>{t.beneficiary?.customer?.mobile_number ?? "—"}</td>
                      <td>{t.beneficiary?.bank_name ?? "—"}</td>
                      <td>{t.beneficiary?.account_number ?? "—"}</td>
                      <td>₹{t.user_amount}</td>
                      <td>₹{t.commission}</td>
                      <td>
                        <span
                          className={`badge ${
                            t.transaction_status?.toLowerCase() === "success"
                              ? "success"
                              : t.transaction_status?.toLowerCase() ===
                                "pending"
                              ? "pending"
                              : "failed"
                          }`}
                        >
                          {/* Normalise display label */}
                          {t.transaction_status?.charAt(0).toUpperCase() +
                            t.transaction_status?.slice(1).toLowerCase()}
                        </span>
                      </td>
                      <td>{t.transaction_id}</td>
                      <td>{new Date(t.transaction_date).toLocaleString()}</td>

                      {/* Action */}
                      <td>
                        {isPending ? (
                          <button
                            style={btn.reject}
                            disabled={isRejecting}
                            onClick={() => setConfirmTxn(t)}
                          >
                            {/* FIX 2: label was "failed", now correctly "Reject" */}
                            {isRejecting ? "Rejecting…" : "Reject"}
                          </button>
                        ) : (
                          <span style={{ color: "#94a3b8", fontSize: 13 }}>
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {displayRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      style={{ padding: 12, textAlign: "center" }}
                    >
                      No transactions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="table-footer">
          <div>Showing 1 to {displayRows.length} of transactions</div>
          <div className="pagination">
            <button
              className="page-num"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Prev
            </button>
            <div className="page-num active">{page}</div>
            <button className="page-num" onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ── Confirm Reject Popup ── */}
      {confirmTxn && (
        <div style={popup.overlay}>
          <div style={popup.modal}>
            <div style={popup.title}>Reject Transaction?</div>
            <div style={popup.body}>
              <div>
                This will mark the transaction as <strong>failed</strong> and
                cannot be undone.
              </div>
              <div style={{ marginTop: 10 }}>
                <span style={{ color: "#64748b" }}>Transaction:</span>{" "}
                <strong>#{confirmTxn.transactions_id}</strong>
              </div>
              <div>
                <span style={{ color: "#64748b" }}>Beneficiary:</span>{" "}
                <strong>
                  {confirmTxn.beneficiary?.account_holder_name ?? "—"}
                </strong>
              </div>
              <div>
                <span style={{ color: "#64748b" }}>Amount:</span>{" "}
                <strong>₹{confirmTxn.user_amount}</strong>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <button style={btn.rejectConfirm} onClick={handleRejectConfirm}>
                Yes, Reject
              </button>
              <button style={btn.cancel} onClick={() => setConfirmTxn(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inline styles ─────────────────────────────────────────────────────────────

const btn: Record<string, React.CSSProperties> = {
  reject: {
    background: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca",
    padding: "5px 12px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
  },
  rejectConfirm: {
    background: "#dc2626",
    color: "#fff",
    border: "none",
    padding: "9px 18px",
    borderRadius: 7,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
  },
  cancel: {
    background: "transparent",
    color: "#64748b",
    border: "1px solid #e2e8f0",
    padding: "9px 18px",
    borderRadius: 7,
    cursor: "pointer",
    fontSize: 14,
  },
};

const popup: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  modal: {
    background: "#fff",
    borderRadius: 12,
    padding: 28,
    width: 400,
    maxWidth: "92vw",
    boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  title: { fontSize: 17, fontWeight: 700, color: "#1e293b" },
  body: { fontSize: 14, color: "#374151", lineHeight: 1.8 },
};

const fb: Record<string, React.CSSProperties> = {
  err: {
    background: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 12,
    fontSize: 14,
  },
  ok: {
    background: "#f0fdf4",
    color: "#16a34a",
    border: "1px solid #bbf7d0",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 12,
    fontSize: 14,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#16a34a",
    fontSize: 18,
    lineHeight: 1,
  },
};